import React, { useState, useEffect } from 'react';
import axios, { AxiosRequestConfig } from 'axios';

import useTranslations from '@/hooks/useTranslations';
import DangerouslySetHtmlContent from '@/components/DangerouslySetHtmlContent';
import { Locales } from '@/types/Locales';

type Props = {
  id: string;
  lang: Locales;
};

const SELLIGENT_ENDPOINT = `${process.env.GATSBY_API_ORIGIN}/node/backend/cldprxy/chan/selligent-renderer`;

const AXIOS_OPTIONS: Partial<AxiosRequestConfig> = {
  headers: { Accept: 'application/xml' },
};

const getNodeValueFromTagCdata = (xmlDoc, tag) => {
  const childNodes = xmlDoc?.getElementsByTagName(tag)?.[0]?.childNodes ?? [];
  let nodeValue = null;

  for (const node of childNodes) {
    if (node.nodeName === '#cdata-section') {
      nodeValue = node.nodeValue;
      break;
    }
  }

  return nodeValue;
};

const SelligentContentRenderer: React.FC<Props> = (props) => {
  const { id, lang } = props;

  if (!id) {
    return null;
  }

  const { t } = useTranslations();
  const [headContent, setHeadContent] = useState('');
  const [bodyContent, setBodyContent] = useState('');

  let url = `${SELLIGENT_ENDPOINT}?ID=${id}&LANG=${lang}`;

  if (typeof window !== 'undefined') {
    const queryParams = new URLSearchParams(window?.location?.search);
    const hash = queryParams.get('HASH');
    url += hash ? `&HASH=${hash}` : '';
  }

  useEffect(() => {
    const getMarketingMessage = async () => {
      try {
        const response = await axios.get(url, AXIOS_OPTIONS);
        const { data } = response;

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(data.trim(), 'text/xml');

        setHeadContent(getNodeValueFromTagCdata(xmlDoc, 'HEAD'));
        setBodyContent(getNodeValueFromTagCdata(xmlDoc, 'BODY'));
      } catch (error) {
        setBodyContent(`
          <div style="font-size: 2rem; display: flex; justify-content: center; align-items: center; padding: 2rem 0;">
            ${t('generic_error')}
          </div>
        `);
      }
    };

    if (id) {
      getMarketingMessage();
    }
  }, [id]);

  useEffect(() => {
    if (headContent) {
      const headFragment = document
        .createRange()
        .createContextualFragment(headContent);

      document.head.appendChild(headFragment);
    }
  }, [headContent]);

  return <DangerouslySetHtmlContent html={bodyContent} />;
};

export default SelligentContentRenderer;
